import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

// WP React stuff
import { useState, useRef } from '@wordpress/element';

/**
 * ChartLine Component
 *
 * Renders a responsive line chart using Chart.js with:
 * - Custom tooltip callbacks
 * - Vertical line on hover
 * - Accepts external custom chart options via `customOptions`
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Chart data and tooltip config
 * @param {string[]} props.data.labels - Array of label strings for the x-axis
 * @param {Array<{label: string, data: number[], color: string}>} props.data.dataSets - Series data with colors
 * @param {Object} props.data.tooltip - Tooltip callbacks
 * @param {Function} props.data.tooltip.title - Function to generate the tooltip title
 * @param {Function} props.data.tooltip.label - Function to generate the tooltip label
 * @param {Function} props.data.tooltip.callbackOnShow - Optional side effect on tooltip show
 * @param {Object} [props.customOptions={}] - Custom Chart.js configuration overrides
 *
 * @returns {JSX.Element|null} Line chart component
 */
const ChartLine = ({ data, customOptions = {} }) => {
  const chartRef = useRef(null);
  const [tooltipDataIndex, setTooltipDataIndex] = useState();

  const options = {
    ...customOptions,
    layout: {
      padding: { top: 0, bottom: 0 },
    },
    borderWidth: 4,
    type: "line",
    responsive: true,
    aspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    stacked: false,
    plugins: {
      title: { display: false },
      legend: { display: false },
      labels: { display: false },
      tooltips: { mode: "x", intersect: false }, // legacy support
      tooltip: {
        bodyAlign: "center",
        titleAlign: "center",
        titleMarginBottom: 0,
        callbacks: {
          title: (tooltipItem) => data.tooltip.title(tooltipItem),
          label: (tooltipItem) => data.tooltip.label(tooltipItem),
          footer: () => false,
          beforeLabel: (tooltipItem) => {
            if (Number(tooltipItem.dataIndex) !== Number(tooltipDataIndex)) {
              setTooltipDataIndex(tooltipItem.dataIndex);
              data.tooltip.callbackOnShow(tooltipItem);
            }
          },
        },
      },
      ...(customOptions?.plugins || {}),
    },
    scales: {
      y: {
        display: false,
      },
      x: {
        display: false,
        grid: { display: true },
        ticks: { display: false },
        ...(customOptions?.scales?.x || {}),
      },
    },
  };

  const chartData = {
    labels: data.labels,
    datasets: data.dataSets.map((item) => ({
      ...item,
      pointBorderWidth: 6,
      fill: false,
      tension: 0.4,
      borderColor: item.color,
      pointBackgroundColor: item.color,
    })),
  };

  /**
   * Custom plugin to render vertical guide line on hover.
   */
  const customPlugins = {
    beforeDraw: (chart) => {
      if (chart.tooltip._active && chart.tooltip._active.length) {
        const activePoint = chart.tooltip._active[0];
        const { ctx } = chart;
        const { x } = activePoint.element;
        const topY = chart.scales.y.top;
        const bottomY = chart.scales.y.bottom;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "#DEE0E2";
        ctx.stroke();
        ctx.restore();
      }
    },
  };

  return (
    data && (
      <div className="chart-line">
        <Line
          options={options}
          data={chartData}
          ref={chartRef}
          plugins={[customPlugins]}
        />
      </div>
    )
  );
};

export default ChartLine;
